import { apiCallPost } from './api';
import {
  HTTPForbidden,
  HTTPTimeoutError,
  HTTPUnauthorized,
  extractResponseBodyErrorMessage,
  resolveHttpErrorMessage,
} from '../execeptions/httpErrors';
import { APIError } from '../execeptions/apiError';
import { notifyUnauthorized } from './unauthorizedHandler';

/**
 * Normalized API result used by sagas.
 * @typedef {{ status: boolean, data?: *, errMsg?: string, responseCode?: number }} ApiResult
 */

function unwrapHttpError(err) {
  if (err instanceof HTTPUnauthorized || err instanceof HTTPForbidden) {
    return err;
  }
  if (err instanceof APIError && err.baseError) {
    return err.baseError;
  }
  return err;
}

function extractBusinessErrorMessage(raw, defaultError) {
  if (!raw || typeof raw !== 'object') {
    return defaultError;
  }
  return extractResponseBodyErrorMessage(raw) || defaultError;
}

function extractErrorMessage(err, defaultError) {
  const httpErr = unwrapHttpError(err);

  if (httpErr instanceof HTTPUnauthorized) {
    return resolveHttpErrorMessage(httpErr) || 'Session expired';
  }
  if (httpErr instanceof HTTPForbidden) {
    return resolveHttpErrorMessage(httpErr) || "You don't have access";
  }
  if (httpErr instanceof HTTPTimeoutError) {
    return httpErr.message || 'Request timed out';
  }
  if (httpErr?.responseCode) {
    return resolveHttpErrorMessage(httpErr);
  }
  if (typeof err?.message === 'string' && err.message) {
    return err.message;
  }
  return defaultError;
}

/**
 * POST JSON and return a consistent shape for sagas.
 *
 * @param {string} path - from apiPaths
 * @param {object} body
 * @param {object} authHeaders
 * @param {{ defaultError?: string, isSuccess?: (res) => boolean, getErrorMessage?: (res) => string }} [options]
 * @returns {Promise<ApiResult>}
 */
export async function postJson(path, body, authHeaders, options = {}) {
  const {
    defaultError = 'Request failed',
    isSuccess = (res) => !!res,
    getErrorMessage = (res) => extractBusinessErrorMessage(res, defaultError),
  } = options;

  try {
    const res = await apiCallPost(path, body, authHeaders);
    if (isSuccess(res)) {
      return { status: true, data: res };
    }
    return {
      status: false,
      data: {},
      errMsg: getErrorMessage(res),
    };
  } catch (err) {
    const httpErr = unwrapHttpError(err);
    if (httpErr instanceof HTTPUnauthorized) {
      notifyUnauthorized();
      return {
        status: false,
        data: {},
        errMsg: 'Session expired',
        responseCode: 401,
      };
    }

    return {
      status: false,
      data: {},
      errMsg: extractErrorMessage(err, defaultError),
      responseCode: httpErr?.responseCode,
    };
  }
}
